import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { ArrowLeft, Send, Lock, Eye, EyeOff, Copy, ExternalLink, Check, KeyRound } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { toast } from 'sonner';
import apiClient from '@/services/apiClient';

const DispatchReportsAdmin = () => {
  const navigate = useNavigate();
  const [pinConfigured, setPinConfigured] = useState(false);
  const [newPin, setNewPin] = useState('');
  const [showPin, setShowPin] = useState(false);
  const [saving, setSaving] = useState(false);
  const [copied, setCopied] = useState(false);

  const reportUrl = `${window.location.origin}/reports/dispatch`;

  useEffect(() => {
    apiClient.get('/management/pin-status')
      .then(res => setPinConfigured(res.data.configured))
      .catch(() => {});
  }, []);

  const handleSetPin = async () => {
    if (!newPin.trim() || newPin.trim().length < 6) {
      toast.error('PIN must be at least 6 digits');
      return;
    }
    setSaving(true);
    try {
      await apiClient.post('/management/set-pin', { pin: newPin.trim() });
      toast.success('PIN updated successfully');
      setPinConfigured(true);
      setNewPin('');
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to set PIN');
    } finally {
      setSaving(false);
    }
  };

  const copyLink = () => {
    navigator.clipboard.writeText(reportUrl);
    setCopied(true);
    toast.success('Link copied');
    setTimeout(() => setCopied(false), 2000);
  };

  return (
    <div className="min-h-screen bg-[#FFFBF5]">
      <header className="bg-[#1A1F2E] text-[#F9FAFB] shadow-lg">
        <div className="max-w-4xl mx-auto px-4 md:px-6 h-16 flex items-center gap-3">
          <Button variant="ghost" className="text-[#F9FAFB] hover:bg-white/10 p-2" onClick={() => navigate('/admin/dashboard')} data-testid="back-to-admin">
            <ArrowLeft className="w-5 h-5" />
          </Button>
          <Send className="w-6 h-6" />
          <div>
            <h1 className="text-lg font-bold">Dispatch Section Reports</h1>
            <p className="text-xs text-[#E8DCC8]">Manage access for management team</p>
          </div>
        </div>
      </header>

      <main className="max-w-4xl mx-auto px-4 md:px-6 py-8 space-y-6">
        {/* Shareable Link Card */}
        <Card className="border-[#E5DFD0]">
          <CardHeader>
            <CardTitle className="flex items-center gap-2 text-[#1A1F2E]">
              <ExternalLink className="w-5 h-5 text-teal-600" />
              Shareable Report Link
            </CardTitle>
            <CardDescription>Share this link with management. They'll need the PIN to access.</CardDescription>
          </CardHeader>
          <CardContent>
            <div className="flex items-center gap-2">
              <div className="flex-1 bg-gray-50 border border-[#E5DFD0] rounded-lg px-4 py-3 text-sm text-[#1A1F2E] font-mono truncate" data-testid="report-link">
                {reportUrl}
              </div>
              <Button variant="outline" className="flex-shrink-0" onClick={copyLink} data-testid="copy-link-btn">
                {copied ? <Check className="w-4 h-4 text-green-600" /> : <Copy className="w-4 h-4" />}
              </Button>
              <Button className="flex-shrink-0 bg-teal-600 hover:bg-teal-700" onClick={() => window.open('/reports/dispatch', '_blank')} data-testid="open-report-btn">
                <ExternalLink className="w-4 h-4 mr-1.5" />
                Open
              </Button>
            </div>
          </CardContent>
        </Card>

        {/* PIN Management Card */}
        <Card className="border-[#E5DFD0]">
          <CardHeader>
            <CardTitle className="flex items-center gap-2 text-[#1A1F2E]">
              <KeyRound className="w-5 h-5 text-amber-600" />
              Access PIN
            </CardTitle>
            <CardDescription>
              {pinConfigured
                ? 'A PIN is currently set. Enter a new PIN below to change it.'
                : 'No PIN configured yet. Set a PIN to enable report access.'}
            </CardDescription>
          </CardHeader>
          <CardContent>
            <div className="flex items-center gap-2">
              <div className="flex-1 relative">
                <Input
                  type={showPin ? 'text' : 'password'}
                  inputMode="numeric"
                  value={newPin}
                  onChange={(e) => setNewPin(e.target.value.replace(/\D/g, '').slice(0, 8))}
                  placeholder={pinConfigured ? 'Enter new 6-digit PIN' : 'Set a 6-digit PIN'}
                  className="pr-10"
                  data-testid="admin-pin-input"
                />
                <button
                  type="button"
                  onClick={() => setShowPin(!showPin)}
                  className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600"
                  data-testid="admin-pin-toggle"
                >
                  {showPin ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                </button>
              </div>
              <Button
                onClick={handleSetPin}
                disabled={saving || !newPin.trim()}
                className="bg-amber-600 hover:bg-amber-700 flex-shrink-0"
                data-testid="admin-pin-save"
              >
                <Lock className="w-4 h-4 mr-1.5" />
                {saving ? 'Saving...' : pinConfigured ? 'Change PIN' : 'Set PIN'}
              </Button>
            </div>
            {pinConfigured && (
              <p className="text-xs text-green-600 mt-2 flex items-center gap-1">
                <Check className="w-3 h-3" /> PIN is active — management can access reports
              </p>
            )}
          </CardContent>
        </Card>
      </main>
    </div>
  );
};

export default DispatchReportsAdmin;
